import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { usePayment } from "../contexts/PaymentContext";
import styles from "./PaymentHistoryPage.module.css";

const PLAN_LABELS = {
  credits: "Starter Pack — 50 Credits",
  unlimited: "Unlimited Pro — 30 Days",
};

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatAmount(paise) {
  return `₹${(paise / 100).toFixed(0)}`;
}

export default function PaymentHistoryPage() {
  const { currentUser } = useAuth();
  const { status, statusLoading, fetchStatus, triggerUpgrade } = usePayment();
  const navigate = useNavigate();
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      navigate("/login");
      return;
    }
    fetchStatus().finally(() => setInitialLoad(false));
  }, [currentUser, navigate, fetchStatus]);

  if (initialLoad || statusLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loader}>Loading...</div>
      </div>
    );
  }

  if (!status) return null;

  const isUnlimited = status.unlimited?.active === true;
  const credits = status.credits ?? 0;
  const dailyUsed = status.freeUsage?.used ?? 0;
  const dailyLimit = status.freeUsage?.limit ?? 5;
  const dailyRemaining = Math.max(0, dailyLimit - dailyUsed);
  const payments = status.payments ?? [];

  return (
    <div className={styles.page}>
      <div className={styles.container}>

        {/* Back link */}
        <Link to="/" className={styles.backLink}>← Back to Generator</Link>

        <h1 className={styles.title}>💳 Account & Billing</h1>

        {/* ── ACTIVE PLAN CARD ── */}
        <section className={styles.planSection}>
          <h2 className={styles.sectionTitle}>Current Plan</h2>

          {isUnlimited ? (
            <div className={`${styles.planCard} ${styles.planCardPro}`}>
              <div className={styles.planCardHeader}>
                <span className={styles.planIcon}>⚡</span>
                <div>
                  <h3 className={styles.planName}>Unlimited Pro</h3>
                  <p className={styles.planMeta}>
                    Expires {formatDate(status.unlimited.expiresAt)}
                  </p>
                </div>
              </div>
              <span className={styles.planBadgeActive}>Active</span>
            </div>
          ) : credits > 0 ? (
            <div className={`${styles.planCard} ${styles.planCardCredits}`}>
              <div className={styles.planCardHeader}>
                <span className={styles.planIcon}>🎯</span>
                <div>
                  <h3 className={styles.planName}>Starter Pack</h3>
                  <p className={styles.planMeta}>
                    {credits} credit{credits !== 1 ? "s" : ""} remaining
                  </p>
                </div>
              </div>
              <span className={styles.planBadgeActive}>Active</span>
            </div>
          ) : (
            <div className={`${styles.planCard} ${styles.planCardFree}`}>
              <div className={styles.planCardHeader}>
                <span className={styles.planIcon}>✨</span>
                <div>
                  <h3 className={styles.planName}>Free Plan</h3>
                  <p className={styles.planMeta}>
                    {dailyRemaining} of {dailyLimit} AI messages left today
                  </p>
                </div>
              </div>
              <button
                className={styles.upgradeBtn}
                onClick={() => triggerUpgrade({ message: "Choose a plan that works for you" })}
              >
                Upgrade
              </button>
            </div>
          )}
        </section>

        {/* ── USAGE STATS ── */}
        <section className={styles.statsSection}>
          <h2 className={styles.sectionTitle}>Today's Usage</h2>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <span className={styles.statValue}>{dailyUsed}</span>
              <span className={styles.statLabel}>Used today</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statValue}>
                {isUnlimited ? "∞" : credits > 0 ? credits : dailyRemaining}
              </span>
              <span className={styles.statLabel}>
                {isUnlimited ? "Remaining" : credits > 0 ? "Credits left" : "Free left"}
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statValue}>
                {isUnlimited ? "Unlimited" : credits > 0 ? "Credits" : "Free"}
              </span>
              <span className={styles.statLabel}>Active tier</span>
            </div>
          </div>
        </section>

        {/* ── PAYMENT HISTORY TABLE ── */}
        <section className={styles.historySection}>
          <h2 className={styles.sectionTitle}>Payment History</h2>

          {payments.length === 0 ? (
            <div className={styles.emptyHistory}>
              <span className={styles.emptyIcon}>📄</span>
              <p>No payments yet.</p>
              <button
                className={styles.upgradeBtn}
                onClick={() => triggerUpgrade({ message: "Choose a plan that works for you" })}
              >
                View Plans
              </button>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Plan</th>
                      <th>Amount</th>
                      <th>Order ID</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p._id}>
                        <td>{formatDate(p.createdAt)}</td>
                        <td>{PLAN_LABELS[p.plan] || p.plan}</td>
                        <td className={styles.amountCell}>{formatAmount(p.amount)}</td>
                        <td className={styles.orderIdCell}>
                          <span title={p.razorpayOrderId}>
                            {p.razorpayOrderId?.slice(-10)}
                          </span>
                        </td>
                        <td>
                          <span className={`${styles.statusPill} ${
                            p.status === "paid" ? styles.statusPaid : styles.statusPending
                          }`}>
                            {p.status === "paid" ? "✓ Paid" : p.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className={styles.mobileCards}>
                {payments.map((p) => (
                  <div key={p._id} className={styles.mobileCard}>
                    <div className={styles.mobileCardRow}>
                      <span className={styles.mobileLabel}>Plan</span>
                      <span className={styles.mobileValue}>{PLAN_LABELS[p.plan] || p.plan}</span>
                    </div>
                    <div className={styles.mobileCardRow}>
                      <span className={styles.mobileLabel}>Amount</span>
                      <span className={styles.mobileValue}>{formatAmount(p.amount)}</span>
                    </div>
                    <div className={styles.mobileCardRow}>
                      <span className={styles.mobileLabel}>Date</span>
                      <span className={styles.mobileValue}>{formatDate(p.createdAt)}</span>
                    </div>
                    <div className={styles.mobileCardRow}>
                      <span className={styles.mobileLabel}>Order</span>
                      <span className={styles.mobileValue} title={p.razorpayOrderId}>
                        {p.razorpayOrderId?.slice(-10)}
                      </span>
                    </div>
                    <div className={styles.mobileCardRow}>
                      <span className={styles.mobileLabel}>Status</span>
                      <span className={`${styles.statusPill} ${
                        p.status === "paid" ? styles.statusPaid : styles.statusPending
                      }`}>
                        {p.status === "paid" ? "✓ Paid" : p.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>

      </div>
    </div>
  );
}
